/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The AI score of a lead or deal.
 *
 * `AiScoreChip` (default) sits in a detail page header: the score and band,
 * a popover with the reasons and when it was scored, and "Score with AI" /
 * "Re-score" for users who may use the AI Assistant. Renders nothing when the
 * tenant has not bought it, or when there is no score and the user cannot
 * make one.
 *
 * `ScoreBadge` is the compact form for a list column.
 *
 * The score lives beside the record (ai_record_scores); it never changes the
 * lead's own score or the deal's probability.
 */

import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Chip, CircularProgress, Popover, Tooltip } from "@mui/material";
import { AutoAwesome, Refresh } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import colors from "styles/tokens/colors";
import useAiRecordScores from "./useAiRecordScores";

const BAND = {
  hot: { label: "Hot", ...colors.status.warning },
  warm: { label: "Warm", ...colors.status.info },
  cold: { label: "Cold", ...colors.status.neutral },
};

// The solid white "Score with AI" on a coloured detail header: same on hover
// and focus, so the label never drops to light-on-light.
const HEADER_BUTTON_SX = {
  bgcolor: colors.background.white,
  color: colors.text.dark,
  "&:hover, &:focus-visible": {
    bgcolor: colors.background.white,
    color: colors.text.dark,
    boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.55)",
  },
  "&:disabled, &.Mui-disabled": { bgcolor: "rgba(255, 255, 255, 0.85)", color: colors.text.dark },
};

function bandStyle(band) {
  return BAND[band] || BAND.cold;
}

function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function ScoreBadge({ score }) {
  if (!score) {
    return (
      <MDTypography variant="caption" color="text">
        -
      </MDTypography>
    );
  }
  const style = bandStyle(score.band);
  return (
    <Tooltip title={(score.reasons || []).join(" · ") || style.label} arrow>
      <Chip
        size="small"
        label={`${score.score} ${style.label}`}
        sx={{
          height: 22,
          fontSize: "0.72rem",
          fontWeight: 600,
          bgcolor: style.background,
          color: style.text,
          opacity: score.stale ? 0.7 : 1,
        }}
      />
    </Tooltip>
  );
}

ScoreBadge.propTypes = {
  score: PropTypes.shape({
    score: PropTypes.number,
    band: PropTypes.string,
    reasons: PropTypes.arrayOf(PropTypes.string),
    stale: PropTypes.bool,
  }),
};

ScoreBadge.defaultProps = { score: null };

export default function AiScoreChip({ objectApiName, recordId, tone }) {
  const ids = useMemo(() => (recordId ? [Number(recordId)] : []), [recordId]);
  const { enabled, canScore, scores, scoring, scoreOne } = useAiRecordScores(objectApiName, ids);
  const [anchor, setAnchor] = useState(null);
  const [error, setError] = useState("");
  const score = scores[Number(recordId)];

  if (!enabled || !recordId || (!score && !canScore)) return null;

  const run = async () => {
    setError("");
    const result = await scoreOne(recordId);
    if (!result.ok) setError(result.message);
  };

  if (!score) {
    return (
      <MDBox display="inline-flex" alignItems="center" gap={1}>
        <MDButton
          variant={tone === "header" ? "contained" : "outlined"}
          color={tone === "header" ? "white" : "info"}
          size="small"
          onClick={run}
          disabled={scoring}
          startIcon={scoring ? <CircularProgress size={12} color="inherit" /> : <AutoAwesome />}
          sx={tone === "header" ? HEADER_BUTTON_SX : undefined}
        >
          {scoring ? "Scoring..." : "Score with AI"}
        </MDButton>
        {error && (
          <Tooltip title={error} arrow>
            <Chip size="small" label="Not scored" sx={{ bgcolor: colors.error.background, color: colors.error.text }} />
          </Tooltip>
        )}
      </MDBox>
    );
  }

  const style = bandStyle(score.band);
  return (
    <>
      <Chip
        icon={<AutoAwesome sx={{ fontSize: 14, color: `${style.text} !important` }} />}
        label={`AI score ${score.score} · ${style.label}`}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={`AI score ${score.score}, ${style.label}. Show reasons`}
        sx={{
          bgcolor: style.background,
          color: style.text,
          fontWeight: 600,
          cursor: "pointer",
          border: "1px solid transparent",
          // A clickable Chip swaps in the theme's grey on hover and focus,
          // which loses the band colour and the text with it. Keep the band.
          "&:hover, &.Mui-focusVisible, &:focus-visible": {
            bgcolor: style.background,
            color: style.text,
            borderColor: style.text,
            filter: "brightness(0.96)",
          },
        }}
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { p: 2, maxWidth: 360, bgcolor: "background.paper" } }}
      >
        <MDTypography variant="button" fontWeight="bold" display="block" sx={{ mb: 1 }}>
          Why {score.score}?
        </MDTypography>
        {(score.reasons || []).length ? (
          <MDBox component="ul" sx={{ m: 0, pl: 2.5 }}>
            {score.reasons.map((reason) => (
              <li key={reason}>
                <MDTypography variant="caption" component="span">
                  {reason}
                </MDTypography>
              </li>
            ))}
          </MDBox>
        ) : (
          <MDTypography variant="caption" color="text">
            No reasons were given.
          </MDTypography>
        )}
        <MDTypography variant="caption" color="text" display="block" sx={{ mt: 1.5 }}>
          Scored {formatWhen(score.scored_at)}
          {score.stale ? ". The record has changed since." : "."} This score does not change the
          record&apos;s own fields.
        </MDTypography>
        {error && (
          <MDTypography variant="caption" display="block" sx={{ mt: 1, color: colors.error.text }}>
            {error}
          </MDTypography>
        )}
        {canScore && (
          <MDBox mt={1.5} display="flex" justifyContent="flex-end">
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              onClick={run}
              disabled={scoring}
              startIcon={scoring ? <CircularProgress size={12} color="inherit" /> : <Refresh />}
            >
              {scoring ? "Scoring..." : "Re-score"}
            </MDButton>
          </MDBox>
        )}
      </Popover>
    </>
  );
}

AiScoreChip.propTypes = {
  objectApiName: PropTypes.oneOf(["leads", "deals"]).isRequired,
  recordId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // "header": on a coloured detail-page header, so the button is solid white.
  tone: PropTypes.oneOf(["default", "header"]),
};

AiScoreChip.defaultProps = { recordId: null, tone: "default" };
